import { useEffect, useState } from 'react';
import {
  getRepresentantOpportunities,
  RepresentantCompanyOpportunities,
  RepresentantStateOpportunities,
} from 'services/actions/representant_dashboard';
import { RepresentedTypes } from 'store/modules/profile_store/types';
import { CompanyCount, companyCounts } from './useRepresentantDashboard';

export type OpportunityMetric = 'hoje' | 'vigentes' | 'iminencia' | 'baixa_concorrencia';

const METRICS: OpportunityMetric[] = ['hoje', 'vigentes', 'iminencia', 'baixa_concorrencia'];

const sumStates = (companies: RepresentantCompanyOpportunities[]) => {
  const byState = new Map<number, RepresentantStateOpportunities>();
  companies.forEach(({ estados }) =>
    (estados || []).forEach((state) => {
      const current = byState.get(state.estado_id);
      byState.set(state.estado_id, {
        ...state,
        total: (current ? current.total : 0) + state.total,
      });
    }),
  );
  return Array.from(byState.values()).sort((a, b) => b.total - a.total);
};

export function useRepresentantOpportunities(companies: RepresentedTypes[]) {
  const enabled = companies.length > 1;
  const companiesKey = companies.map(({ id }) => id).join(',');
  const [data, setData] = useState<RepresentantCompanyOpportunities[]>();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setData(undefined);
    setError(false);
    getRepresentantOpportunities(companiesKey.split(',').map(Number))
      .then((response) => active && setData(response.data.empresas))
      .catch(() => active && setError(true));

    return () => {
      active = false;
    };
  }, [enabled, companiesKey]);

  const breakdown = (metric: OpportunityMetric): CompanyCount[] =>
    data
      ? companyCounts(companies, (id) => data.find((company) => company.id === id)?.[metric])
      : [];

  return {
    enabled,
    loading: enabled && !data && !error,
    error: (metric: OpportunityMetric) =>
      error || (!!data && data.every((company) => company[metric] === null)),
    breakdown: Object.fromEntries(METRICS.map((metric) => [metric, breakdown(metric)])) as Record<
      OpportunityMetric,
      CompanyCount[]
    >,
    states: data ? sumStates(data) : [],
  };
}
